/**
 * Courier Inbox — partner order emails awaiting review.
 *
 * The review queue IS the page, so NEEDS_REVIEW is the default filter rather than
 * "all". An email sitting unnoticed here is an order nobody is delivering, and
 * unlike a failed API call nothing else in the system will raise its hand.
 *
 * The detail dialog puts parsed fields on the left and the ORIGINAL EMAIL on the
 * right. Side-by-side is the whole point: a wrong address or a mangled item is
 * obvious in two seconds when you can see both, and nearly invisible when you have
 * to trust a form.
 */
import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, CircularProgress, Tabs, Tab, Alert,
  Divider, Tooltip, TextField, Grid,
} from '@mui/material'
import DataTable from 'react-data-table-component'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ScienceIcon from '@mui/icons-material/Science'
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead'

const COURIER_EMAILS = gql`
  query CourierEmails($status: String, $limit: Int, $offset: Int) {
    courierEmails(status: $status, limit: $limit, offset: $offset) {
      total
      countsByStatus
      emails {
        _id merchantName fromAddress toAddress subject receivedAt
        messageKind parserVersion confidence detectedKind status
        reviewReason courierOrderId error createdAt
      }
    }
  }
`

const COURIER_EMAIL = gql`
  query CourierEmail($id: ID!) {
    courierEmail(id: $id) {
      _id merchantName fromAddress toAddress subject receivedAt authResults
      rawHtml messageKind parserVersion confidence parsed detectedKind
      status reviewReason courierOrderId error
    }
  }
`

const REPARSE = gql`
  mutation Reparse($id: ID!) {
    courierReparseEmail(id: $id) { ok status kind confidence blockers warnings parsed }
  }
`
const IMPORT = gql`
  mutation Import($id: ID!, $force: Boolean) {
    courierImportEmail(id: $id, force: $force) {
      created reason blockers
      order { _id status orderKind total externalOrderNumber }
    }
  }
`
const IGNORE = gql`
  mutation Ignore($id: ID!, $reason: String) {
    courierIgnoreEmail(id: $id, reason: $reason) { _id status }
  }
`
const TEST_PARSE = gql`
  mutation TestParse($html: String!) {
    courierTestParse(html: $html) { ok status kind confidence blockers warnings parsed }
  }
`

const statusStyle = {
  RECEIVED:     { bg: '#e0f2fe', color: '#0369a1' },
  PARSED:       { bg: '#dcfce7', color: '#14532d' },
  NEEDS_REVIEW: { bg: '#fef3c7', color: '#92400e' },
  IMPORTED:     { bg: '#ede9fe', color: '#5b21b6' },
  IGNORED:      { bg: '#f1f5f9', color: '#475569' },
  FAILED:       { bg: '#fee2e2', color: '#991b1b' },
}
const kindStyle = {
  DELIVERY: { bg: '#dbeafe', color: '#1e40af' },
  PICKUP:   { bg: '#f0fdf4', color: '#166534' },
  TAKEOUT:  { bg: '#f0fdf4', color: '#166534' },
  DINE_IN:  { bg: '#f1f5f9', color: '#475569' },
  UNKNOWN:  { bg: '#fef3c7', color: '#92400e' },
}

const Pill = ({ label, styles }) => (
  <Chip
    size="small"
    label={label || '—'}
    sx={{ bgcolor: styles?.bg || '#f1f5f9', color: styles?.color || '#475569', fontWeight: 600 }}
  />
)

const TABS = ['NEEDS_REVIEW', 'RECEIVED', 'PARSED', 'IMPORTED', 'FAILED', 'IGNORED']
const PAGE_SIZE = 50

const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`)
const when = (s) => (s ? new Date(s).toLocaleString() : '—')

export default function CourierInbox() {
  const [tab, setTab] = useState(0)
  const [page, setPage] = useState(1)
  const [openId, setOpenId] = useState(null)
  const [testOpen, setTestOpen] = useState(false)
  const [testHtml, setTestHtml] = useState('')
  const [actionMsg, setActionMsg] = useState(null)

  const status = TABS[tab]

  const { data, loading, refetch } = useQuery(COURIER_EMAILS, {
    variables: { status, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  })

  const detail = useQuery(COURIER_EMAIL, { variables: { id: openId }, skip: !openId, fetchPolicy: 'network-only' })

  const [reparse, reparseState] = useMutation(REPARSE)
  const [doImport, importState] = useMutation(IMPORT)
  const [doIgnore] = useMutation(IGNORE)
  const [testParse, testState] = useMutation(TEST_PARSE)

  const emails = data?.courierEmails?.emails ?? []
  const total = data?.courierEmails?.total ?? 0
  const counts = useMemo(() => {
    try { return JSON.parse(data?.courierEmails?.countsByStatus || '{}') } catch { return {} }
  }, [data])

  const email = detail.data?.courierEmail
  const parsed = useMemo(() => {
    try { return email?.parsed ? JSON.parse(email.parsed) : null } catch { return null }
  }, [email])
  const validation = parsed?._validation

  const testResult = testState.data?.courierTestParse
  const testParsed = useMemo(() => {
    try { return testResult?.parsed ? JSON.parse(testResult.parsed) : null } catch { return null }
  }, [testResult])

  const runAction = async (fn, label) => {
    setActionMsg(null)
    try {
      const res = await fn()
      const payload = res?.data?.courierImportEmail
      if (payload && payload.created === false) {
        setActionMsg({ severity: 'warning', text: `${label}: ${payload.reason || 'nothing was created'}` })
      } else if (payload?.order) {
        setActionMsg({ severity: 'success', text: `Imported as courier order #${payload.order._id} (${payload.order.status})` })
      } else {
        setActionMsg({ severity: 'success', text: `${label} done` })
      }
      await refetch()
      if (openId) await detail.refetch()
    } catch (e) {
      setActionMsg({ severity: 'error', text: `${label} failed: ${e.message}` })
    }
  }

  const columns = [
    {
      name: 'Received', width: '160px', sortable: true,
      selector: (r) => r.receivedAt,
      cell: (r) => <span style={{ fontSize: 13 }}>{when(r.receivedAt)}</span>,
    },
    { name: 'Merchant', width: '150px', cell: (r) => r.merchantName || <em style={{ color: '#b45309' }}>unmatched</em> },
    {
      name: 'Subject', grow: 2,
      cell: (r) => <span style={{ fontSize: 13 }} title={r.subject}>{r.subject || '—'}</span>,
    },
    { name: 'Kind', width: '140px', cell: (r) => <Pill label={r.detectedKind} styles={kindStyle[r.detectedKind]} /> },
    {
      name: 'Conf', width: '80px',
      // Confidence is the MINIMUM of the fields dispatch needs, not an average —
      // so anything under 1 means a required field is missing outright.
      cell: (r) => (r.confidence == null ? '—' : (
        <span style={{ fontWeight: 700, color: Number(r.confidence) >= 1 ? '#14532d' : '#92400e' }}>
          {Number(r.confidence).toFixed(2)}
        </span>
      )),
    },
    { name: 'Status', width: '175px', cell: (r) => <Pill label={r.status} styles={statusStyle[r.status]} /> },
    {
      name: 'Why', grow: 2,
      cell: (r) => <span style={{ fontSize: 12, color: '#92400e' }}>{r.reviewReason || r.error || ''}</span>,
    },
    {
      name: '', width: '60px',
      cell: (r) => (
        <IconButton size="small" onClick={() => { setOpenId(r._id); setActionMsg(null) }}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ]

  const Field = ({ label, value, warn }) => (
    <Box sx={{ mb: 1.2 }}>
      <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: warn ? '#b45309' : '#0f172a', wordBreak: 'break-word' }}>
        {value == null || value === '' ? <em style={{ color: '#b45309' }}>missing</em> : String(value)}
      </Typography>
    </Box>
  )

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <MarkEmailReadIcon sx={{ color: '#FF3008' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Courier Inbox</Typography>
        <Typography sx={{ color: '#64748b', fontSize: 14 }}>partner order emails</Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<ScienceIcon />} onClick={() => setTestOpen(true)} sx={{ textTransform: 'none' }}>
          Test-parse an email
        </Button>
        <IconButton onClick={() => refetch()}><RefreshIcon /></IconButton>
      </Box>

      {actionMsg && <Alert severity={actionMsg.severity} sx={{ mb: 2 }} onClose={() => setActionMsg(null)}>{actionMsg.text}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(1) }} variant="scrollable" scrollButtons="auto">
          {TABS.map((s) => (
            <Tab
              key={s}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              label={`${s.replace('_', ' ')}${counts[s] ? ` (${counts[s]})` : ''}`}
            />
          ))}
        </Tabs>
      </Paper>

      <Paper>
        <DataTable
          columns={columns}
          data={emails}
          progressPending={loading}
          progressComponent={<Box sx={{ py: 4 }}><CircularProgress /></Box>}
          pagination
          paginationServer
          paginationTotalRows={total}
          paginationPerPage={PAGE_SIZE}
          paginationDefaultPage={page}
          onChangePage={setPage}
          noDataComponent={<Box sx={{ py: 5, color: '#64748b' }}>Nothing in {status.replace('_', ' ').toLowerCase()}</Box>}
          highlightOnHover
          dense
        />
      </Paper>

      {/* ── Detail: parsed fields | original email ─────────────────────────── */}
      <Dialog open={!!openId} onClose={() => setOpenId(null)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <span>{email?.subject || 'Email'}</span>
            <Pill label={email?.status} styles={statusStyle[email?.status]} />
            <Pill label={email?.detectedKind} styles={kindStyle[email?.detectedKind]} />
          </Box>
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5 }}>
            {email?.merchantName || 'unmatched merchant'} · from {email?.fromAddress} · to {email?.toAddress} · {when(email?.receivedAt)}
            {email?.parserVersion ? ` · parser ${email.parserVersion}` : ''}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {detail.loading && <CircularProgress />}

          {validation?.blockers?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Not safe to dispatch automatically:</strong>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {validation.blockers.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </Alert>
          )}
          {validation?.warnings?.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {validation.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </Alert>
          )}
          {email?.error && <Alert severity="error" sx={{ mb: 2 }}>{email.error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>What we read</Typography>
              {!parsed && <Typography sx={{ color: '#64748b' }}>Not parsed yet — press Re-parse.</Typography>}
              {parsed && (
                <>
                  <Field label="Order number" value={parsed.externalOrderNumber} />
                  <Field label="Order type" value={`${parsed.orderKind || '—'}  (tag: ${parsed.paymentTagKind || '—'})`} />
                  <Field label="Promise time" value={parsed.promiseTimeRaw} />
                  {validation?.resolved?.promiseTime && (
                    <Field label="Resolved due" value={when(validation.resolved.promiseTime)} />
                  )}
                  {validation?.resolved?.dispatchAt && (
                    <Field label="Would dispatch at" value={when(validation.resolved.dispatchAt)} />
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  <Field label="Customer" value={parsed.customer?.name} />
                  <Field label="Phone" value={validation?.resolved?.phone || parsed.customer?.phone} />
                  <Field label="Email" value={parsed.customer?.email} />
                  <Field label="Dropoff" value={parsed.address?.raw} />
                  {validation?.resolved?.dropoff && (
                    <Field
                      label="Geocoded"
                      value={`${validation.resolved.dropoff.lat}, ${validation.resolved.dropoff.lng}`
                        + (validation.resolved.dropoff.milesFromMerchant != null
                          ? ` · ${validation.resolved.dropoff.milesFromMerchant} mi from restaurant` : '')}
                    />
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', mb: 0.5 }}>Items</Typography>
                  {(parsed.items || []).map((it, i) => (
                    <Box key={i} sx={{ mb: 0.8 }}>
                      <Typography sx={{ fontSize: 14 }}>
                        {it.quantity}× {it.title} <strong>{money(it.unitPrice)}</strong>
                      </Typography>
                      {(it.modifiers || []).map((m, j) => (
                        <Typography key={j} sx={{ fontSize: 13, color: '#475569', pl: 2 }}>
                          – {m.title} {money(m.price)}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ fontSize: 14 }}>
                    {[
                      ['Sub total', parsed.money?.subTotal], ['Delivery fee', parsed.money?.deliveryFee],
                      ['Processing fee', parsed.money?.processingFee], ['Tax', parsed.money?.tax],
                      ['Tip (driver)', parsed.money?.tip], ['Total', parsed.money?.total],
                    ].map(([l, v]) => (
                      <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
                        <span style={{ color: '#64748b' }}>{l}</span>
                        <strong>{money(v)}</strong>
                      </Box>
                    ))}
                    {validation?.resolved?.totals && (
                      <Box sx={{ mt: 1 }}>
                        {/* Totals reconciling is the single best proxy for "the parse
                            is right" — a dropped line stops the arithmetic closing. */}
                        <Chip
                          size="small"
                          label={validation.resolved.totals.ok
                            ? 'Totals reconcile ✓'
                            : `Totals off: lines sum to ${money(validation.resolved.totals.expected)}`}
                          sx={{
                            bgcolor: validation.resolved.totals.ok ? '#dcfce7' : '#fee2e2',
                            color: validation.resolved.totals.ok ? '#14532d' : '#991b1b',
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Grid>

            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700 }}>Original email</Typography>
                <Tooltip title="The email exactly as it arrived. Compare against the left column — a wrong address or a mangled item shows up immediately.">
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>(as received)</Typography>
                </Tooltip>
              </Box>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 560, overflow: 'auto', bgcolor: '#fff' }}>
                {/* Rendered in a sandboxed iframe: this is third-party HTML from an
                    external sender, so it must not be able to run script or read
                    the admin session. srcDoc + an empty sandbox blocks both. */}
                <iframe
                  title="original email"
                  sandbox=""
                  srcDoc={email?.rawHtml || '<p style="font-family:sans-serif;color:#64748b">No HTML body</p>'}
                  style={{ width: '100%', height: 540, border: 0 }}
                />
              </Paper>
              {email?.authResults && (
                <Typography sx={{ fontSize: 11, color: '#64748b', mt: 1, wordBreak: 'break-all' }}>
                  auth: {email.authResults}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ gap: 1 }}>
          {/* Every handler below closes over openId at click time. Guarded rather
              than trusted, because the Dialog's own open={!!openId} can start
              closing (backdrop click, Escape) in the same moment a button click
              lands — without this, that race sent id:null to a non-null GraphQL
              variable and the mutation errored outright instead of just quietly
              doing nothing, which is all a click against a closing dialog should
              ever do. */}
          <Button onClick={() => openId && runAction(() => doIgnore({ variables: { id: openId } }), 'Ignore')} sx={{ textTransform: 'none' }}>
            Ignore
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => openId && runAction(() => reparse({ variables: { id: openId } }), 'Re-parse')}
            disabled={reparseState.loading}
            sx={{ textTransform: 'none' }}
          >
            Re-parse
          </Button>
          {/* Force is offered only when something is blocking, and the imported
              order still lands in NEEDS_REVIEW — forcing can never quietly make an
              order dispatchable. */}
          {validation?.blockers?.length > 0 && (
            <Button
              color="warning"
              onClick={() => openId && runAction(() => doImport({ variables: { id: openId, force: true } }), 'Force import')}
              disabled={importState.loading}
              sx={{ textTransform: 'none' }}
            >
              Import anyway
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => openId && runAction(() => doImport({ variables: { id: openId } }), 'Import')}
            disabled={importState.loading || email?.status === 'IMPORTED'}
            sx={{ textTransform: 'none' }}
          >
            {email?.status === 'IMPORTED' ? 'Already imported' : 'Import'}
          </Button>
          <Button onClick={() => setOpenId(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Test-parse: onboard a new template without a deploy ───────────── */}
      <Dialog open={testOpen} onClose={() => setTestOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Test-parse an email</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, color: '#64748b', mb: 1.5 }}>
            Paste the email HTML (in Gmail: ⋮ → Show original → copy the HTML part). Nothing is stored and
            no order is created — this is for checking a new merchant&apos;s template before turning their
            auto-dispatch on.
          </Typography>
          <TextField
            multiline minRows={8} maxRows={14} fullWidth value={testHtml}
            onChange={(e) => setTestHtml(e.target.value)}
            placeholder="<div>…email HTML…</div>"
            sx={{ fontFamily: 'monospace' }}
          />
          {testResult && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <Pill label={testResult.kind} styles={kindStyle[testResult.kind]} />
                <Chip size="small" label={`confidence ${testResult.confidence ?? '—'}`} />
                <Chip
                  size="small"
                  label={testResult.ok ? 'would pass' : 'would be held for review'}
                  sx={{ bgcolor: testResult.ok ? '#dcfce7' : '#fef3c7', fontWeight: 600 }}
                />
              </Box>
              {testResult.blockers?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <ul style={{ margin: '0 0 0 18px' }}>{testResult.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </Alert>
              )}
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
                <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(
                    testParsed && {
                      orderNumber: testParsed.externalOrderNumber,
                      kind: testParsed.orderKind,
                      promise: testParsed.promiseTimeRaw,
                      customer: testParsed.customer,
                      address: testParsed.address?.raw,
                      items: (testParsed.items || []).map((i) => `${i.quantity}x ${i.title} $${i.unitPrice}`),
                      money: testParsed.money,
                    },
                    null, 2
                  )}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
          <Button
            variant="contained"
            disabled={!testHtml.trim() || testState.loading}
            onClick={() => testParse({ variables: { html: testHtml } })}
            sx={{ textTransform: 'none' }}
          >
            Parse
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
